import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { StsExpense, StsExpenseApiLine, StsPrivateClient } from './sts-models';

const BOLLO_DESCRIPTION_PREFIX = 'Imposta di Bollo';
const INVOICE_TIPO = 'Parcella Privati';

const DEFAULT_IVA_OPTION = '0% - N1';

/**
 * Maps `GET /invoicing/sts/:id` (`StsExpense`) to a reactive `FormGroup`
 * for the read-only review page opened from the invoices list.
 *
 * Inverse of `buildStsInvoicePayload` in `sts-invoice-form.mapper.ts` (form → API).
 */
export function buildStsExpenseFormFromApi(
  fb: FormBuilder,
  invoice: StsExpense,
  privateClients: StsPrivateClient[]
): FormGroup {
  const form = createEmptyStsExpenseForm(fb);
  populateStsExpenseForm(form, fb, invoice, privateClients);
  return form;
}

function createEmptyStsExpenseForm(fb: FormBuilder): FormGroup {
  const form = fb.group({
    tipo: ['', Validators.required],
    client_id: [null, Validators.required],
    cliente: [null, Validators.required],
    riferimento: [null, [Validators.maxLength(200)]],
    progressivo_numero: [
      null,
      [Validators.required, Validators.pattern(/^\d{3}$/)],
    ],
    progressivo_anno: [null, Validators.required],
    data: [null, Validators.required],
    pagata: [false],
    scadenza: [null, Validators.required],
    pagamento: [null, Validators.required],
    cassa_previdenza: [true],
    tipologia_cassa: [null],
    cassa_previdenza_codice_iva: [null],
    percentuale_cassa: [null],
    applica_bollo: [false],
    escludi_bollo: [false],
    imponibile: [{ value: null, disabled: true }],
    totale_cassa_previdenza: [{ value: null, disabled: true }],
    imposta: [{ value: 0, disabled: true }],
    bollo: [{ value: null, disabled: true }],
    totale: [{ value: null, disabled: true }],
    totale_ritenuta: [{ value: null, disabled: true }],
    linee_documento: fb.array([]),
    doctor_bank_name: [null],
    doctor_bank_iban: [null],
    flag_opposizione: [false],
  });

  form.patchValue({ tipo: INVOICE_TIPO });
  form.get('tipo')?.disable();
  return form;
}

function populateStsExpenseForm(
  form: FormGroup,
  fb: FormBuilder,
  invoice: StsExpense,
  privateClients: StsPrivateClient[]
): void {
  const formattedDate = invoice.data
    ? moment(invoice.data, 'DD/MM/YYYY').toDate()
    : null;

  let cassa_previdenza_codice_iva: string | null = null;
  if (invoice.cassa_previdenza_natura_iva) {
    cassa_previdenza_codice_iva = `0% - ${invoice.cassa_previdenza_natura_iva}`;
  } else if (invoice.cassa_previdenza_iva != null) {
    cassa_previdenza_codice_iva = `${invoice.cassa_previdenza_iva}%`;
  }

  form.patchValue({
    tipo: invoice.tipo || INVOICE_TIPO,
    client_id: invoice.client_id || null,
    cliente: resolveClientName(invoice.client_id, privateClients),
    riferimento: invoice.riferimento || null,
    progressivo_numero: invoice.progressivo_numero || null,
    progressivo_anno: invoice.progressivo_anno || null,
    data: formattedDate,
    pagata: invoice.pagata ?? false,
    scadenza: invoice.scadenza || null,
    pagamento: invoice.pagamento || null,
    cassa_previdenza: invoice.cassa_previdenza ?? false,
    tipologia_cassa: invoice.tipologia_cassa || null,
    percentuale_cassa: invoice.percentuale_cassa ?? null,
    cassa_previdenza_codice_iva,
    applica_bollo: invoice.applica_bollo ?? false,
    escludi_bollo: invoice.escludi_bollo ?? false,
    imponibile: invoice.imponibile ?? null,
    totale_cassa_previdenza: invoice.totale_cassa_previdenza ?? null,
    imposta: invoice.imposta ?? 0,
    bollo: invoice.bollo ?? null,
    totale: invoice.totale ?? null,
    doctor_bank_name: invoice.doctor_bank_name || null,
    doctor_bank_iban: invoice.doctor_bank_iban || null,
    flag_opposizione: invoice.flag_opposizione ?? false,
  });

  const linesArray = form.get('linee_documento') as FormArray;
  while (linesArray.length) {
    linesArray.removeAt(0);
  }

  if (invoice.linee_documento?.length) {
    invoice.linee_documento.forEach((line) => {
      if (isBolloLineFromApi(line)) {
        const bolloLine = createBolloLine(
          fb,
          line.descrizione || BOLLO_DESCRIPTION_PREFIX
        );
        bolloLine.get('iva_option')?.setValue(toIvaOption(line));
        bolloLine
          .get('totale')
          ?.setValue(Number(line.totale).toFixed(2), { emitEvent: false });
        linesArray.push(bolloLine);
        return;
      }

      const lineGroup = createDocumentLine(fb);
      lineGroup.patchValue({
        descrizione: line.descrizione || '',
        quantita: String(line.quantita ?? '1'),
        prezzo: Number(line.prezzo).toFixed(2),
        iva_option: toIvaOption(line),
        totale: Number(line.totale).toFixed(2),
      });
      linesArray.push(lineGroup);
    });
  }

  form.markAsPristine();
}

function toIvaOption(line: StsExpenseApiLine): string {
  if (line.natura_iva) {
    return `0% - ${line.natura_iva}`;
  }
  return `${line.iva}%`;
}

function resolveClientName(
  clientId: string,
  privateClients: StsPrivateClient[]
): string | null {
  const client = privateClients.find((c) => c.id === clientId);
  if (!client) {
    return null;
  }
  return `${client.client_first_name} ${client.client_last_name}`;
}

function isBolloLineFromApi(line: StsExpenseApiLine): boolean {
  if (line.is_bollo) {
    return true;
  }
  return line.descrizione?.startsWith(BOLLO_DESCRIPTION_PREFIX) ?? false;
}

function createDocumentLine(fb: FormBuilder): FormGroup {
  return fb.group({
    is_bollo: [false],
    descrizione: [''],
    quantita: ['1.00'],
    prezzo: ['0.00'],
    iva_option: [DEFAULT_IVA_OPTION],
    totale: [{ value: '0.00', disabled: true }],
  });
}

function createBolloLine(
  fb: FormBuilder,
  descrizione: string = BOLLO_DESCRIPTION_PREFIX
): FormGroup {
  return fb.group({
    is_bollo: [true],
    descrizione: [{ value: descrizione, disabled: true }],
    quantita: [{ value: '1.00', disabled: true }],
    prezzo: [{ value: '2.00', disabled: true }],
    iva_option: [{ value: DEFAULT_IVA_OPTION, disabled: false }],
    totale: [{ value: '2.00', disabled: true }],
  });
}
